import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import DialogContent from '@mui/material/DialogContent';
import { DownloadGameDialogHeader } from './DownloadGameDialogHeader';
import { DownloadGameInfo } from './DownloadGameInfo';
import { GradientIcon } from '../gradients/GradientIcon';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { OperatingSystemRequirementsListProps } from '../system/OperatingSystemRequirementsList';
import { Fragment } from 'react';

// implementation fits only 2 Operating Systems
/**
 * TODO:
 * - change from grid to flex
 * - add side-scrolling or carousel for more OSs
 * - edit requirement props from system (might not be necessary)
 * - review design of modal (talk to @luiz.k on Slack for more info)
 */

export type OperatingSystemRequirementsWithDownload =
  OperatingSystemRequirementsListProps & {
    downloadUrl: string;
    coverImageUrl: string;
  };

export type DownloadGameDialogProps = {
  title: string;
  recRequirements: OperatingSystemRequirementsWithDownload[];
  version: string;
  // use a state hook to pass these values,
  // when the dialog is closed, or the user clicks outside of it
  // onClose will be called
  open: boolean;
  onClose: () => void;
};

export function DownloadGameDialog({
  title,
  recRequirements,
  version,
  open,
  onClose,
}: DownloadGameDialogProps) {
  const operatingSystems = recRequirements.slice(0, 2);
  return (
    <Dialog
      onClose={() => onClose()}
      fullWidth
      maxWidth="lg"
      open={open}
      sx={{ '.MuiPaper-root': { height: '640px' } }}
    >
      <DialogContent
        sx={{
          overflowY: 'auto',
        }}
      >
        <DownloadGameDialogHeader
          title={title}
          recRequirements={recRequirements}
        />
        <Box
          display="grid"
          gridTemplateColumns="1fr 0.1fr 1fr"
          gridTemplateRows="1fr"
          gap={0}
          paddingTop="20px"
        >
          {operatingSystems.map(
            (
              {
                downloadUrl,
                requirements,
                coverImageUrl,
                name: OperatingSystem,
              },
              index,
            ) => {
              return (
                <Fragment key={`${OperatingSystem}`}>
                  <Box
                    sx={{
                      gridArea: index === 0 ? '1 / 1 / 2 / 2' : '1 / 3 / 2 / 4',
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'center',
                      justifyContent: 'center',
                      justifySelf: 'center',
                      gap: '16px',
                      alignItems: 'center',
                      maxWidth: '335px',
                    }}
                  >
                    <DownloadGameInfo
                      title={title}
                      version={version}
                      downloadUrl={downloadUrl}
                      requirements={requirements}
                      coverImageUrl={coverImageUrl}
                      name={OperatingSystem}
                    />
                  </Box>
                  {index !== operatingSystems.length - 1 && (
                    <Box
                      sx={{
                        gridArea: '1 / 2 / 2 / 3',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Divider
                        sx={{
                          width: '1px',
                          height: '120%',
                          background:
                            'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0) 0%, #FFFFFF 49.22%, rgba(255, 255, 255, 0) 100%)',
                        }}
                        orientation="vertical"
                      />
                    </Box>
                  )}
                </Fragment>
              );
            },
          )}
        </Box>
      </DialogContent>
      <Box
        onClick={() => onClose()}
        sx={{
          position: 'absolute',
          zIndex: 2,
          top: 0,
          right: 0,
          mx: 6,
          my: 4,
        }}
      >
        <GradientIcon
          gradientColor="action.mainActive"
          IconComponent={CancelRoundedIcon}
          sx={{
            ':hover': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    </Dialog>
  );
}
